<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration, withAlpha } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 500));
const accent = computed(() => props.block.accentColor || "#a78bfa");

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "radial-gradient(ellipse at 30% 30%, #0d0a2c 0%, #02010c 80%)",
      fontFamily:
        props.block.fontFamily || "'Space Grotesk', 'Inter', sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-accent": accent.value,
      "--cd-glow": withAlpha(accent.value, 0.5),
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.5}px`,
    height: `${base * 2.5}px`,
    fontSize: `${base}px`,
    radius: `${props.block.cardRadius ?? 16}px`,
  };
});

const nodes = Array.from({ length: 6 }, (_, i) => i);
</script>

<template>
  <div class="cd-ai" :style="containerStyle">
    <svg class="cd-ai__neural" viewBox="0 0 400 200" aria-hidden="true">
      <g
        stroke="var(--cd-accent)"
        stroke-width="0.5"
        opacity="0.55"
        fill="none"
      >
        <line x1="40" y1="40" x2="200" y2="100" />
        <line x1="40" y1="100" x2="200" y2="40" />
        <line x1="40" y1="160" x2="200" y2="100" />
        <line x1="200" y1="40" x2="360" y2="100" />
        <line x1="200" y1="100" x2="360" y2="40" />
        <line x1="200" y1="100" x2="360" y2="160" />
      </g>
      <g fill="var(--cd-accent)">
        <circle cx="40" cy="40" r="3" />
        <circle cx="40" cy="100" r="3" />
        <circle cx="40" cy="160" r="3" />
        <circle cx="200" cy="40" r="3" />
        <circle cx="200" cy="100" r="3" />
        <circle cx="360" cy="40" r="3" />
        <circle cx="360" cy="100" r="3" />
        <circle cx="360" cy="160" r="3" />
      </g>
    </svg>

    <div class="cd-ai__data" aria-hidden="true">
      <span
        v-for="i in nodes"
        :key="i"
        :style="{ animationDelay: `${i * 0.5}s` }"
      />
    </div>

    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-ai__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        animationDelay: `${i * 110}ms`,
      }"
    >
      <span class="cd-ai__halo" />
      <span class="cd-ai__inner">
        <span
          class="cd-ai__digit"
          :key="seg.value"
          :style="{
            fontSize: cardSize.fontSize,
            color: block.digitColor || '#ffffff',
          }"
        >
          {{ seg.value }}
        </span>
        <span
          class="cd-ai__label"
          :style="{
            fontSize: `${block.labelFontSize}px`,
            color: block.labelColor || withAlpha(accent, 0.85),
          }"
        >
          {{ seg.label }}
        </span>
      </span>
    </div>
  </div>
</template>

<style scoped>
.cd-ai {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  overflow: hidden;
  isolation: isolate;
}
.cd-ai__neural {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.35;
  filter: drop-shadow(0 0 4px var(--cd-glow));
  pointer-events: none;
}
.cd-ai__neural circle {
  animation: cd-ai-pulse 2.5s ease-in-out infinite;
}
.cd-ai__neural circle:nth-child(2n) {
  animation-delay: 0.4s;
}
.cd-ai__neural circle:nth-child(3n) {
  animation-delay: 0.8s;
}
@keyframes cd-ai-pulse {
  0%,
  100% {
    transform-origin: center;
    opacity: 0.45;
    r: 2.5;
  }
  50% {
    opacity: 1;
    r: 4;
  }
}
.cd-ai__data {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.cd-ai__data span {
  position: absolute;
  top: 0;
  height: 70%;
  width: 1px;
  background: linear-gradient(
    180deg,
    transparent,
    var(--cd-accent),
    transparent
  );
  filter: drop-shadow(0 0 4px var(--cd-glow));
  opacity: 0;
  animation: cd-ai-stream 4s linear infinite;
}
.cd-ai__data span:nth-child(1) {
  left: 18%;
}
.cd-ai__data span:nth-child(2) {
  left: 32%;
}
.cd-ai__data span:nth-child(3) {
  left: 46%;
}
.cd-ai__data span:nth-child(4) {
  left: 60%;
}
.cd-ai__data span:nth-child(5) {
  left: 74%;
}
.cd-ai__data span:nth-child(6) {
  left: 88%;
}
@keyframes cd-ai-stream {
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  100% {
    transform: translateY(180%);
    opacity: 0;
  }
}
.cd-ai__cell {
  position: relative;
  z-index: 1;
  background: rgba(8, 6, 28, 0.78);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--cd-glow);
  box-shadow:
    0 0 20px var(--cd-glow),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: cd-ai-rise 800ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
}
@keyframes cd-ai-rise {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.cd-ai__halo {
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  background: conic-gradient(
    from 90deg,
    transparent 0%,
    var(--cd-accent) 25%,
    transparent 50%,
    var(--cd-accent) 75%,
    transparent 100%
  );
  filter: blur(8px);
  opacity: 0.65;
  animation: cd-ai-spin 8s linear infinite;
  z-index: 0;
}
@keyframes cd-ai-spin {
  to {
    transform: rotate(360deg);
  }
}
.cd-ai__inner {
  position: relative;
  z-index: 1;
  width: calc(100% - 4px);
  height: calc(100% - 4px);
  border-radius: inherit;
  background: rgba(8, 6, 28, 0.92);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.cd-ai__digit {
  font-weight: 700;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 12px var(--cd-glow);
  display: inline-block;
  animation: cd-ai-tick var(--cd-tick, 500ms) ease-out;
}
@keyframes cd-ai-tick {
  0% {
    opacity: 0;
    transform: scale(1.2);
    filter: blur(4px);
  }
  100% {
    opacity: 1;
    transform: scale(1);
    filter: blur(0);
  }
}
.cd-ai__label {
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-weight: 500;
}
</style>
